import { computed, Ref } from 'vue';
import {
  DeviceRadioCheckStatus,
  OverallRadioCheckStatus,
} from '@/enums/radiocheck';
import i18n from '@/utils/i18n/radiocheck.json';
import { Status } from '@/enums/generic';

const ICONS = {
  checkCircle: 'mdi-check-circle',
  alertCircle: 'mdi-alert-circle',
  helpCircle: 'mdi-help-circle',
  loading: 'mdi-spin mdi-loading',
};

const STATUS_TEXTS = {
  success: i18n.status.success,
  error: i18n.status.error,
  pending: i18n.status.pending,
  unknown: i18n.status.unknown,
};

const TEXT_LABEL = {
  completed: i18n.gwLabel.completed,
  incomplete: i18n.gwLabel.incomplete,
  failed: i18n.gwLabel.failed,
  error: i18n.gwLabel.error,
  pending: i18n.gwLabel.pending,
  timedOut: i18n.gwLabel.timedOut,
  canceled: i18n.gwLabel.canceled,
  undefined: i18n.gwLabel.undefined,
};

const STATUS_DESCRIPTIONS = {
  completed: i18n.info.completed,
  incomplete: i18n.info.incomplete,
  error: i18n.info.error,
  pending: i18n.info.pending,
  failed: i18n.info.failed,
  timedOut: i18n.info.timedOut,
  canceled: i18n.info.canceled,
};

const STATUS_TYPES = {
  success: Status.SUCCESS,
  error: Status.ERROR,
  info: Status.INFO,
  warning: Status.WARNING,
};

const TEXT_COLORS = {
  info: 'text-primary-2 dark:text-primary-2',
  error: 'text-light-error dark:text-dark-error',
  success: 'text-light-success dark:text-dark-success',
  warning: 'text-light-warning dark:text-dark-warning',
  disabled: 'text-dark-disabled-text dark:text-dark-disabled-text',
};

export function useRadioCheckStatus(
  statusRef: Ref<OverallRadioCheckStatus | DeviceRadioCheckStatus>
) {
  function getDeviceRadioCheckStatusProps(status: DeviceRadioCheckStatus) {
    switch (status) {
      case DeviceRadioCheckStatus.OK:
      case DeviceRadioCheckStatus.WARNING:
        return {
          icon: ICONS.checkCircle,
          type: STATUS_TYPES.success,
          text: STATUS_TEXTS.success,
          color: TEXT_COLORS.success,
          gwColor: TEXT_COLORS.success,
          label: TEXT_LABEL.completed,
        };
      case DeviceRadioCheckStatus.ERROR:
      case DeviceRadioCheckStatus.CRITICAL:
        return {
          icon: ICONS.alertCircle,
          type: STATUS_TYPES.error,
          text: STATUS_TEXTS.error,
          color: TEXT_COLORS.error,
          gwColor: TEXT_COLORS.error,
          label: TEXT_LABEL.error,
        };
      case DeviceRadioCheckStatus.PENDING:
        return {
          icon: ICONS.loading,
          type: STATUS_TYPES.info,
          text: STATUS_TEXTS.pending,
          color: TEXT_COLORS.info,
          gwColor: TEXT_COLORS.info,
          label: TEXT_LABEL.pending,
        };
      case DeviceRadioCheckStatus.TIMED_OUT:
        return {
          icon: ICONS.helpCircle,
          type: STATUS_TYPES.warning,
          text: STATUS_DESCRIPTIONS.timedOut,
          color: TEXT_COLORS.warning,
          gwColor: TEXT_COLORS.warning,
          label: TEXT_LABEL.timedOut,
        };
      default:
        return {
          icon: ICONS.helpCircle,
          type: STATUS_TYPES.warning,
          text: STATUS_TEXTS.unknown,
          color: TEXT_COLORS.warning,
          gwColor: TEXT_COLORS.warning,
          label: TEXT_LABEL.undefined,
        };
    }
  }

  function getOverallRadioCheckStatusProps(status: OverallRadioCheckStatus) {
    switch (status) {
      case OverallRadioCheckStatus.COMPLETED:
        return {
          icon: ICONS.checkCircle,
          type: STATUS_TYPES.success,
          text: STATUS_DESCRIPTIONS.completed,
          color: TEXT_COLORS.success,
          gwColor: TEXT_COLORS.success,
          label: TEXT_LABEL.completed,
        };
      case OverallRadioCheckStatus.INCOMPLETE:
        return {
          icon: ICONS.helpCircle,
          type: STATUS_TYPES.warning,
          text: STATUS_DESCRIPTIONS.incomplete,
          color: TEXT_COLORS.warning,
          gwColor: TEXT_COLORS.warning,
          label: TEXT_LABEL.incomplete,
        };
      case OverallRadioCheckStatus.FAILED:
        return {
          icon: ICONS.alertCircle,
          type: STATUS_TYPES.error,
          text: STATUS_DESCRIPTIONS.failed,
          color: TEXT_COLORS.error,
          gwColor: TEXT_COLORS.error,
          label: TEXT_LABEL.failed,
        };
      case OverallRadioCheckStatus.ERROR:
        return {
          icon: ICONS.alertCircle,
          type: STATUS_TYPES.error,
          text: STATUS_DESCRIPTIONS.error,
          color: TEXT_COLORS.error,
          gwColor: TEXT_COLORS.error,
          label: TEXT_LABEL.error,
        };
      case OverallRadioCheckStatus.PENDING:
        return {
          icon: ICONS.loading,
          type: STATUS_TYPES.info,
          text: STATUS_DESCRIPTIONS.pending,
          color: TEXT_COLORS.info,
          gwColor: TEXT_COLORS.disabled,
          label: TEXT_LABEL.pending,
        };
      case OverallRadioCheckStatus.TIMED_OUT:
        return {
          icon: ICONS.helpCircle,
          type: STATUS_TYPES.warning,
          text: STATUS_DESCRIPTIONS.timedOut,
          color: TEXT_COLORS.warning,
          gwColor: TEXT_COLORS.disabled,
          label: TEXT_LABEL.timedOut,
        };
      case OverallRadioCheckStatus.CANCELED:
        return {
          icon: ICONS.helpCircle,
          type: STATUS_TYPES.warning,
          text: STATUS_DESCRIPTIONS.canceled,
          color: TEXT_COLORS.warning,
          gwColor: TEXT_COLORS.disabled,
          label: TEXT_LABEL.canceled,
        };
      default:
        return {
          icon: ICONS.helpCircle,
          type: STATUS_TYPES.warning,
          text: STATUS_TEXTS.unknown,
          color: TEXT_COLORS.warning,
          gwColor: TEXT_COLORS.disabled,
          label: TEXT_LABEL.undefined,
        };
    }
  }

  const statusProps = computed(() => {
    const statusValue = statusRef.value;
    if (isOverallRadioCheckStatus(statusValue)) {
      return getOverallRadioCheckStatusProps(statusValue);
    }
    if (isDeviceRadioCheckStatus(statusValue)) {
      return getDeviceRadioCheckStatusProps(statusValue);
    }

    return {
      icon: ICONS.helpCircle,
      text: STATUS_TEXTS.unknown,
      type: STATUS_TYPES.warning,
      color: TEXT_COLORS.warning,
      gwColor: TEXT_COLORS.disabled,
      label: TEXT_LABEL.undefined,
    };
  });

  return {
    statusIcon: computed(() => statusProps.value.icon),
    statusText: computed(() => statusProps.value.text),
    statusType: computed(() => statusProps.value.type),
    statusTextColor: computed(() => statusProps.value.color),
    statusGwLabel: computed(() => statusProps.value.label),
    statusGwColor: computed(() => statusProps.value.gwColor),
  };

  function isOverallRadioCheckStatus(
    value: any
  ): value is OverallRadioCheckStatus {
    return Object.values(OverallRadioCheckStatus).includes(value);
  }

  function isDeviceRadioCheckStatus(
    value: any
  ): value is DeviceRadioCheckStatus {
    return Object.values(DeviceRadioCheckStatus).includes(value);
  }
}
